import React, { useCallback, useRef, useState } from "react";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { endOfMonth, set, setDate, setMonth, subMonths } from "date-fns";

import ButtonPrimary from "@src/multiCustodian/components/MUI/Button/Button";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import DatePicker from "@src/sharedComponents/DatePicker/DatePicker";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import Popover from "@src/sharedComponents/Popover/Popover";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./DateRangeFilter.module.css";

export type FiltersAppliedSchema = {
  dateFrom?: Date;
  dateTo?: Date;
};

const zeroOutDate = (d: Date) =>
  set(d, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

export type DateRangeOption =
  | "All"
  | "Current Month"
  | "Previous Month"
  | "Year-to-date"
  | "Custom";

const dateRangeOptions: DropdownMenuOption<DateRangeOption>[] = [
  { id: "All", label: "All" },
  { id: "Current Month", label: "Current Month" },
  { id: "Previous Month", label: "Previous Month" },
  { id: "Year-to-date", label: "Year-to-date" },
  { id: "Custom", label: "Custom" },
];

interface DateRangeFilterProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FiltersAppliedSchema) => void;
  currentFilters: FiltersAppliedSchema;
  triggerElement: React.ReactNode;
  dateRangeOption: DateRangeOption;
  onDateRangeOptionChange: (option: DateRangeOption) => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  isOpen,
  onClose,
  onApply,
  currentFilters,
  triggerElement,
  dateRangeOption,
  onDateRangeOptionChange,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  const [tempFilters, setTempFilters] =
    useState<FiltersAppliedSchema>(currentFilters);

  const getDateRange = useCallback(
    (dateRangeOption: DateRangeOption): { from: Date; to: Date } | null => {
      const today = zeroOutDate(new Date());

      switch (dateRangeOption) {
        case "All":
          return null;
        case "Current Month":
          return {
            from: setDate(today, 1),
            to: today,
          };
        case "Previous Month":
          return {
            from: subMonths(setDate(today, 1), 1),
            to: zeroOutDate(endOfMonth(subMonths(setDate(today, 1), 1))),
          };
        case "Year-to-date":
          return {
            from: setMonth(setDate(today, 1), 0),
            to: today,
          };
        case "Custom":
          return null; // Custom dates are handled separately
        default:
          return null;
      }
    },
    []
  );

  const handleDateRangeOptionChange = (
    option: DropdownMenuOption<DateRangeOption>
  ) => {
    const selectedOption = option.id;
    onDateRangeOptionChange(selectedOption);

    if (selectedOption === "Custom") {
      // Keep existing custom dates if any
      return;
    }

    if (selectedOption === "All") {
      // Clear all date filters when "All" is selected
      setTempFilters((prev: FiltersAppliedSchema) => ({
        ...prev,
        dateFrom: undefined,
        dateTo: undefined,
      }));
      return;
    }

    const dateRange = getDateRange(selectedOption);
    setTempFilters((prev: FiltersAppliedSchema) => ({
      ...prev,
      dateFrom: dateRange?.from,
      dateTo: dateRange?.to,
    }));
  };

  const handleDateFromChange = (date: Date | null) => {
    setTempFilters((prev: FiltersAppliedSchema) => ({
      ...prev,
      dateFrom: date ?? undefined,
    }));
  };

  const handleDateToChange = (date: Date | null) => {
    setTempFilters((prev: FiltersAppliedSchema) => ({
      ...prev,
      dateTo: date ?? undefined,
    }));
  };

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const handleCancel = () => {
    setTempFilters(currentFilters);
    onClose();
  };

  return (
    <>
      <div ref={anchorRef}>{triggerElement}</div>
      <Popover
        open={isOpen}
        onClose={handleCancel}
        anchor={anchorRef.current}
        transformOriginVertical={-10}
        popoverElement={<div />}
      >
        <div className={styles.container}>
          {/* Date Range Section */}
          <div className={styles.section}>
            <Text variant="body" color="text">
              Date Range
            </Text>
            <BaseDropdown
              curItemId={dateRangeOption}
              curItemLabel={
                dateRangeOptions.find((opt) => opt.id === dateRangeOption)
                  ?.label
              }
              onSelect={handleDateRangeOptionChange}
              options={dateRangeOptions}
              placeholder="Select date range"
              width={216}
            />
          </div>

          {/* Custom Date Range Fields */}
          {dateRangeOption === "Custom" && (
            <>
              {/* Divider */}
              <div className={styles.divider} />
              <div className={styles.customDateSection}>
                <Text variant="bodyBold" color="text">
                  Custom Date Range
                </Text>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <div className={styles.dateFieldsContainer}>
                    <div className={styles.dateField}>
                      <Text
                        variant="body"
                        color="text"
                        className={styles.dateFieldLabel}
                      >
                        From
                      </Text>
                      <DatePicker
                        value={tempFilters.dateFrom ?? null}
                        onChange={handleDateFromChange}
                        placeholder="MM/DD/YYYY"
                        helperText="MM/DD/YYYY"
                        className={styles.dateFieldInput}
                        style={{ width: "50%" }}
                      />
                    </div>
                    <div className={styles.dateField}>
                      <Text
                        variant="body"
                        color="text"
                        className={styles.dateFieldLabel}
                      >
                        To
                      </Text>
                      <DatePicker
                        value={tempFilters.dateTo ?? null}
                        onChange={handleDateToChange}
                        placeholder="MM/DD/YYYY"
                        helperText="MM/DD/YYYY"
                        className={styles.dateFieldInput}
                        style={{ width: "50%" }}
                      />
                    </div>
                  </div>
                </LocalizationProvider>
              </div>
            </>
          )}

          {/* Divider */}
          <div className={styles.divider} />

          {/* Buttons */}
          <div className={styles.buttons}>
            <ButtonPrimary
              text="Cancel"
              onClick={handleCancel}
              variant="outlined"
              buttonType="secondary"
              size="small"
            />

            <ButtonPrimary
              text="Apply"
              onClick={handleApply}
              variant="contained"
              buttonType="primary"
              size="small"
            />
          </div>
        </div>
      </Popover>
    </>
  );
};

export default DateRangeFilter;
